import React, { useState } from 'react';
import {
  X,
  History,
  Activity,
  Clock,
  RotateCcw,
  CheckCircle2,
  Lock,
  Unlock,
  Share2,
  Trash2,
  Edit3,
  FilePlus,
  ShieldCheck,
  Eye,
  AlertCircle,
} from 'lucide-react';

export default function NoteActivityModal({
  open,
  onClose,
  note,
  onRestoreVersion,
}) {
  const [activeTab, setActiveTab] = useState('versions'); // 'versions' | 'audit'
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(null);
  const [restoring, setRestoring] = useState(false);

  if (!open || !note) return null;

  const versions = Array.isArray(note.versions) && note.versions.length > 0
    ? note.versions
    : [
        {
          version: 1,
          savedAt: note.updated_at || note.created_at || new Date().toISOString(),
          title: note.title || 'Untitled Note',
          content: note.content || '',
          summary: 'Initial version',
        },
      ];

  const auditLog = Array.isArray(note.auditLog) && note.auditLog.length > 0
    ? note.auditLog
    : [
        {
          action: 'Created',
          actor: 'You (Current Device)',
          timestamp: note.created_at || new Date().toISOString(),
          details: 'Zero-knowledge encrypted note created with AES-256-GCM',
        },
        ...(note.updated_at && note.updated_at !== note.created_at
          ? [
              {
                action: 'Updated',
                actor: 'You (Current Device)',
                timestamp: note.updated_at,
                details: 'Content updated and re-encrypted',
              },
            ]
          : []),
      ];

  const getActionIcon = (action) => {
    switch ((action || '').toLowerCase()) {
      case 'created':
        return <FilePlus size={15} color="#10b981" />;
      case 'updated':
        return <Edit3 size={15} color="#3b82f6" />;
      case 'locked':
        return <Lock size={15} color="#f59e0b" />;
      case 'unlocked':
        return <Unlock size={15} color="#06b6d4" />;
      case 'shared':
        return <Share2 size={15} color="#8b5cf6" />;
      case 'trashed':
      case 'deleted':
        return <Trash2 size={15} color="#ef4444" />;
      case 'restored':
        return <RotateCcw size={15} color="#10b981" />;
      default:
        return <Activity size={15} color="#6366f1" />;
    }
  };

  const handleRestore = async (versionObj) => {
    setRestoring(true);
    try {
      if (onRestoreVersion) {
        await onRestoreVersion(versionObj);
      }
      onClose();
    } catch (err) {
      console.error('Failed to restore version:', err);
    } finally {
      setRestoring(false);
    }
  };

  const currentPreviewVersion = selectedVersionIndex !== null ? versions[selectedVersionIndex] : null;

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal-container glass-panel note-activity-modal note-right-drawer animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Note Activity & Version History"
      >
        <div className="modal-header">
          <div className="modal-header-info">
            <div className="modal-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
              <History size={20} />
            </div>
            <div>
              <h2 className="modal-title">Activity & Version History</h2>
              <p className="modal-subtitle">
                <span className="text-truncate font-semibold" style={{ maxWidth: '280px', display: 'inline-block' }}>
                  {note.title || 'Untitled Note'}
                </span>
                {' · '}Zero-Knowledge Audit Trail
              </p>
            </div>
          </div>
          <button type="button" className="btn-icon modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="note-activity-tabs">
          <button
            type="button"
            className={`note-activity-tab ${activeTab === 'versions' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('versions');
              setSelectedVersionIndex(null);
            }}
          >
            <History size={16} />
            <span>Version History ({versions.length})</span>
          </button>
          <button
            type="button"
            className={`note-activity-tab ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('audit');
              setSelectedVersionIndex(null);
            }}
          >
            <Activity size={16} />
            <span>Audit Trail ({auditLog.length})</span>
          </button>
        </div>

        <div className="modal-body note-activity-body">
          {activeTab === 'versions' ? (
            <div className="note-versions-container">
              {/* Version list */}
              <div className="note-versions-list">
                {versions.map((ver, idx) => {
                  const isCurrent = idx === versions.length - 1;
                  const isSelected = selectedVersionIndex === idx;
                  const dateStr = ver.savedAt ? new Date(ver.savedAt).toLocaleString() : 'Unknown date';

                  return (
                    <div
                      key={ver.version || idx}
                      className={`note-version-item ${isSelected ? 'selected' : ''} ${isCurrent ? 'current-version' : ''}`}
                      onClick={() => setSelectedVersionIndex(idx)}
                    >
                      <div className="note-version-header">
                        <div className="note-version-badge-wrap">
                          <span className="note-version-num">v{ver.version || idx + 1}</span>
                          {isCurrent && <span className="badge badge-success badge-xs">Current</span>}
                        </div>
                        <span className="note-version-date">{dateStr}</span>
                      </div>
                      <p className="note-version-summary">
                        {ver.summary || (ver.title ? `Title: ${ver.title}` : 'Saved revision')}
                      </p>
                      <div className="note-version-footer">
                        <button
                          type="button"
                          className="btn-link btn-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedVersionIndex(idx);
                          }}
                        >
                          <Eye size={12} />
                          <span>Preview</span>
                        </button>
                        {!isCurrent && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-xs"
                            disabled={restoring}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestore(ver);
                            }}
                          >
                            <RotateCcw size={12} />
                            <span>Restore</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Version preview panel */}
              <div className="note-version-preview-panel">
                {currentPreviewVersion ? (
                  <div className="note-version-preview-content">
                    <div className="note-version-preview-banner">
                      <div>
                        <strong>v{currentPreviewVersion.version || selectedVersionIndex + 1}: {currentPreviewVersion.title || 'Untitled'}</strong>
                        <div className="note-version-preview-time">
                          Saved on {new Date(currentPreviewVersion.savedAt).toLocaleString()}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={restoring}
                        onClick={() => handleRestore(currentPreviewVersion)}
                      >
                        <RotateCcw size={14} />
                        <span>Restore This Version</span>
                      </button>
                    </div>
                    <div className="note-version-preview-text">
                      <pre>{currentPreviewVersion.content || '(Empty content)'}</pre>
                    </div>
                  </div>
                ) : (
                  <div className="note-version-empty-preview">
                    <History size={36} opacity={0.3} />
                    <p>Select any version on the left to preview its content and compare changes.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Audit Log Tab */
            <div className="note-audit-timeline">
              <div className="note-audit-security-notice">
                <ShieldCheck size={16} color="#10b981" />
                <span>All actions are cryptographically signed and stored in your zero-knowledge audit history.</span>
              </div>
              <div className="timeline-items">
                {auditLog.map((log, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-icon-box">
                      {getActionIcon(log.action)}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timeline-action-name">{log.action}</span>
                        <span className="timeline-time">
                          <Clock size={12} />
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="timeline-details">{log.details}</p>
                      {log.actor && (
                        <div className="timeline-actor">Actor: {log.actor}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
